import { Button } from "antd";
import { Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import locationImage from "assets/images/location.png";
import LoadingFullScreen from "pages/login/LoadingFullScreen";
import { checkIn, validateCheckin } from "services/SauceService";

export const Location = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location || {};
  const { bookingId }: { bookingId: string } = state || {};

  const userOS = useMemo(() => {
    const userAgent = navigator.userAgent;

    if (/android/i.test(userAgent)) {
      return "Android";
    }

    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return "iOS";
    }

    return "generic"; // Default for other systems
  }, []);

  const [status, setStatus] = useState<
    | "loading"
    | "error"
    | "request-permission"
    | "error-no-permission"
    | "error-too-far"
  >("loading");

  const doShareLocation = useCallback(() => {
    const onSuccess = async (location: any) => {
      try {
        const response = await validateCheckin({
          bookingId,
          latLong: {
            latitude: location.coords.latitude,
            longitude: location.coords.longitude,
          },
        });

        // 1 success, 2 too far
        if (response.status === 1) {
          await checkIn({
            bookingId,
          });
          navigate(`/welcome/${bookingId}`);
        } else {
          setStatus("error-too-far");
        }
      } catch (error) {
        setStatus("error");
      }
    };

    navigator.geolocation.getCurrentPosition(
      onSuccess,
      (error) => {
        setStatus("error-no-permission");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [bookingId, navigate]);

  const checkLocationPermission = useCallback(async () => {
    const permissionStatus = await navigator.permissions.query({
      name: "geolocation",
    });

    if (permissionStatus.state === "granted") {
      // request and leave
      doShareLocation();
    } else if (permissionStatus.state === "denied") {
      setStatus("error-no-permission");
    } else if (permissionStatus.state === "prompt") {
      // request and leave
      setStatus("request-permission");
    }
  }, [doShareLocation]);

  const doCancel = () => {
    navigate(-1);
  };

  useEffect(() => {
    checkLocationPermission();
  }, [checkLocationPermission]);

  if (status === "loading") {
    return <LoadingFullScreen />;
  }

  if (status === "request-permission") {
    return (
      <div
        className="page-full-screen fixed book-detail collab-detail"
        style={{ overflow: "auto" }}
      >
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
              gap: 60,
            }}
          >
            <div>
              <Typography.Title level={3} className="heading-1-32-bold">
                Share your location
              </Typography.Title>
              <Typography.Text className="subtitle-18-regular-poppins">
                For check-in, location confirmation is required.
              </Typography.Text>
            </div>

            <img src={locationImage} alt="" style={{ width: 220 }} />
          </div>
          <div>
            <Button
              type="primary"
              size="large"
              shape="round"
              onClick={doShareLocation}
              style={{ marginTop: 24, width: "100%" }}
            >
              Share my location
            </Button>
            <Button
              type="link"
              style={{ marginTop: 12, width: "100%", fontWeight: "bold" }}
              onClick={doCancel}
            >
              Cancel
            </Button>
          </div>
        </>
      </div>
    );
  }

  if (status === "error-no-permission") {
    return (
      <div
        className="page-full-screen fixed book-detail collab-detail"
        style={{ overflow: "auto" }}
      >
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
              gap: 60,
            }}
          >
            <div>
              <Typography.Title level={3} className="heading-1-32-bold">
                We need your location!
              </Typography.Title>
              <Typography.Text className="subtitle-18-regular-poppins">
                For check-in, location confirmation is required.
              </Typography.Text>
            </div>

            <Typography.Title level={1} style={{ textAlign: "center" }}>
              ⚠️
            </Typography.Title>

            {userOS === "generic" && (
              <div style={{ padding: "0px 20px" }}>
                <Typography.Title
                  level={5}
                  style={{ textAlign: "center", paddingTop: 0 }}
                >
                  Please enable location access for this site:
                </Typography.Title>
                <ol>
                  <li>
                    Click the <span className="icon">🔒</span> (lock) or{" "}
                    <span className="icon">ℹ️</span> (info) icon in the address
                    bar next to the website URL.
                  </li>
                  <li>Find 'Location' or 'Site settings'.</li>
                  <li>
                    Change the setting for 'Location' from 'Block' to 'Allow'.
                  </li>
                  <li>Reload this page.</li>
                </ol>
              </div>
            )}

            {userOS === "iOS" && (
              <div style={{ padding: "0px 20px" }}>
                <Typography.Title
                  level={5}
                  style={{ textAlign: "center", paddingTop: 0 }}
                >
                  Please enable location access for this site
                </Typography.Title>
                <ol>
                  <li>
                    Go to your device's main <strong>Settings</strong> app.
                  </li>
                  <li>
                    Scroll down and tap on your browser (e.g.,{" "}
                    <strong>Safari</strong> or <strong>Chrome</strong>).
                  </li>
                  <li>
                    Tap <strong>Location</strong>.
                  </li>
                  <li>
                    Set location access for this site to 'Ask' or 'Allow'.
                  </li>
                  <li>Reload this page.</li>
                </ol>

                <p>
                  Once enabled, please click the "Share my location" button
                  again on this page.
                </p>
              </div>
            )}

            {userOS === "Android" && (
              <div style={{ padding: "0px 20px" }}>
                <Typography.Title
                  level={5}
                  style={{ textAlign: "center", paddingTop: 0 }}
                >
                  Please enable location access for this site:
                </Typography.Title>
                <ol>
                  <li>
                    Tap the <span className="icon">🔒</span> (lock) or{" "}
                    <span className="icon">ℹ️</span> (info) icon in the address
                    bar at the top of your screen.
                  </li>
                  <li>Tap 'Permissions' or 'Site settings'.</li>
                  <li>Find 'Location' and set it to 'Allow'.</li>
                  <li>Reload this page.</li>
                  <li>
                    (Optional: Also check your device's main Settings:
                    <strong>
                      Settings &gt; Apps &gt; [Your Browser] &gt; Permissions
                      &gt; Location
                    </strong>
                    to ensure it's allowed at the OS level.)
                  </li>
                </ol>
              </div>
            )}
          </div>
          <div>
            <Button
              type="primary"
              size="large"
              shape="round"
              onClick={doShareLocation}
              style={{ marginTop: 24, width: "100%" }}
            >
              Share my location
            </Button>
            <Button
              type="link"
              style={{ marginTop: 12, width: "100%", fontWeight: "bold" }}
              onClick={doCancel}
            >
              Cancel
            </Button>
          </div>
        </>
      </div>
    );
  }

  if (status === "error-too-far") {
    return (
      <div
        className="page-full-screen fixed book-detail collab-detail"
        style={{ overflow: "auto" }}
      >
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
              gap: 60,
            }}
          >
            <div>
              <Typography.Title level={3} className="heading-1-32-bold">
                You are far from the restaurant!
              </Typography.Title>
              <Typography.Text className="subtitle-18-regular-poppins">
                You need to enter the restaurant for check-in.
              </Typography.Text>
            </div>

            <img src={locationImage} alt="" style={{ width: 220 }} />
          </div>
          <div>
            <Button
              type="primary"
              size="large"
              shape="round"
              style={{ marginTop: 12, width: "100%", fontWeight: "bold" }}
              onClick={doCancel}
            >
              Got it
            </Button>
          </div>
        </>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div
        className="page-full-screen fixed book-detail collab-detail"
        style={{ overflow: "auto" }}
      >
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
              gap: 60,
            }}
          >
            <div>
              <Typography.Title level={3} className="heading-1-32-bold">
                Something went wrong
              </Typography.Title>
              <Typography.Text className="subtitle-18-regular-poppins">
                Something wen't wrong while validating your location. Please try
                again.
              </Typography.Text>
            </div>
          </div>
          <div>
            <Button
              type="primary"
              size="large"
              shape="round"
              onClick={doShareLocation}
              style={{ marginTop: 24, width: "100%" }}
            >
              Retry
            </Button>
            <Button
              type="link"
              style={{ marginTop: 12, width: "100%", fontWeight: "bold" }}
              onClick={doCancel}
            >
              Cancel
            </Button>
          </div>
        </>
      </div>
    );
  }

  return null;
};
